import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { createPortal } from "react-dom";
import { AlertTriangle, Check, MoreVertical, PauseCircle, Pencil, PlayCircle, RefreshCw, RotateCcw, Trash2, Zap } from "lucide-react";
import type { TenantWithSensitive } from "@am/shared";
import { fmtDate } from "@am/shared";
import { useApiClient } from "../api/client";
import { toast } from "sonner";
import { PageHeader } from "../components/PageHeader";
import { ConfirmModal } from "../components/ConfirmModal";
import { KeyDisplayModal } from "../modals/KeyDisplayModal";

interface ConfirmConfig {
  title: React.ReactNode;
  body: React.ReactNode;
  extra?: React.ReactNode;
  okLabel: string;
  danger?: boolean;
  onConfirm: () => Promise<void>;
}

export function TenantsPage() {
  const api = useApiClient();
  const navigate = useNavigate();

  const [tenants, setTenants] = useState<TenantWithSensitive[]>([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [showInactive, setShowInactive] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [openMenuId, setOpenMenuId] = useState<string | null>(null);
  const [confirm, setConfirm] = useState<ConfirmConfig | null>(null);
  const [rotatedKey, setRotatedKey] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const list = await api<TenantWithSensitive[]>(`/admin/api/tenants?include_inactive=${showInactive}`);
      setTenants(list ?? []);
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Erreur de chargement";
      setError(msg);
      toast.error("Erreur chargement tenants : " + msg);
    } finally {
      setLoading(false);
    }
  }, [api, showInactive, toast]);

  useEffect(() => {
    void load();
  }, [load]);

  // Close the row dropdown when clicking outside it.
  useEffect(() => {
    function onDocClick() {
      setOpenMenuId(null);
    }
    document.addEventListener("click", onDocClick);
    return () => document.removeEventListener("click", onDocClick);
  }, []);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return tenants;
    return tenants.filter(
      (t) =>
        t.name.toLowerCase().includes(q) ||
        t.slug.toLowerCase().includes(q) ||
        (t.email ?? "").toLowerCase().includes(q),
    );
  }, [tenants, query]);

  function askRotateKey(t: TenantWithSensitive) {
    setConfirm({
      title: <>Renouveler la clé <em>widget</em></>,
      body: (
        <>
          Voulez-vous renouveler la clé API du tenant <strong>{t.name}</strong> ?
          <br />
          L'ancienne clé sera immédiatement invalidée et le widget du client cessera de fonctionner jusqu'à la mise à jour.
        </>
      ),
      okLabel: "Renouveler",
      onConfirm: async () => {
        const data = await api<{ widget_key: string }>(`/admin/api/tenants/${t.id}/rotate-key`, {
          method: "POST",
        });
        setRotatedKey(data.widget_key);
        setConfirm(null);
        toast.success(`Clé widget renouvelée pour « ${t.name} ».`);
        await load();
      },
    });
  }

  function askDeactivate(t: TenantWithSensitive) {
    setConfirm({
      title: "Désactiver le tenant",
      body: (
        <>
          Le tenant <strong>{t.name}</strong> sera désactivé. Le widget ne répondra plus aux requêtes jusqu'à réactivation.
        </>
      ),
      okLabel: "Désactiver",
      danger: true,
      onConfirm: async () => {
        await api(`/admin/api/tenants/${t.id}/deactivate`, { method: "POST" });
        toast.success(`Tenant « ${t.name} » désactivé.`);
        setConfirm(null);
        await load();
      },
    });
  }

  async function activate(t: TenantWithSensitive) {
    try {
      await api(`/admin/api/tenants/${t.id}/activate`, { method: "POST" });
      toast.success(`Tenant « ${t.name} » réactivé !`);
      await load();
    } catch (err) {
      toast.error("Erreur : " + (err instanceof Error ? err.message : "inconnue"));
    }
  }

  function askDelete(t: TenantWithSensitive) {
    setConfirm({
      title: <>Supprimer le tenant <em>définitivement</em></>,
      body: (
        <>
          Vous êtes sur le point de supprimer <strong>{t.name}</strong>. Cette action est <strong>irréversible</strong>.
        </>
      ),
      extra: (
        <div className="warn-box" style={{ marginTop: 8, display: "flex", gap: 8 }}>
          <AlertTriangle size={14} style={{ flexShrink: 0, marginTop: 2 }} /> La suppression cascade inclut :
          <ul style={{ margin: "8px 0 0 18px", lineHeight: 1.8 }}>
            <li>Cache Redis (réponses chatbot)</li>
            <li>Collection Qdrant (embeddings produits)</li>
            <li>Données analytics (SQLite)</li>
            <li>Enregistrement en base de données</li>
          </ul>
        </div>
      ),
      okLabel: "Supprimer définitivement",
      danger: true,
      onConfirm: async () => {
        await api(`/admin/api/tenants/${t.id}`, { method: "DELETE" });
        toast.success(`Tenant « ${t.name} » supprimé définitivement.`);
        setConfirm(null);
        await load();
      },
    });
  }

  function askFlushCache(t: TenantWithSensitive) {
    setConfirm({
      title: "Vider le cache du tenant",
      body: (
        <>
          Vider le cache Redis de <strong>{t.name}</strong> ? Les prochaines requêtes seront recalculées (impact temporaire sur la latence).
        </>
      ),
      okLabel: "Vider le cache",
      onConfirm: async () => {
        const data = await api<{ deleted_keys?: number }>(
          `/admin/api/cache/flush?tenant_id=${encodeURIComponent(t.id)}`,
          { method: "POST" },
        );
        toast.success(`Cache vidé — ${data?.deleted_keys ?? 0} clé(s) supprimée(s).`);
        setConfirm(null);
      },
    });
  }

  return (
    <div className="page is-active">
      <PageHeader
        layout="split"
        title={<em>Tenants</em>}
        subtitle="Gestion des clients de la plateforme"
      >
        <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
          <input
            type="text"
            placeholder="Rechercher…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            style={{ width: 220, padding: "8px 12px", fontSize: 13 }}
          />
          <label className="check-row" style={{ fontSize: 13 }}>
            <input
              type="checkbox"
              checked={showInactive}
              onChange={(e) => setShowInactive(e.target.checked)}
            />
            Afficher les inactifs
          </label>
          <button type="button" className="btn-sm btn-ghost" onClick={load} style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <RefreshCw size={12} /> Actualiser
          </button>
        </div>
      </PageHeader>

      <div className="card">
        <table>
          <thead>
            <tr>
              <th>Nom / Slug</th>
              <th>Email</th>
              <th>Modèle</th>
              <th>LLM</th>
              <th>Statut</th>
              <th>Créé le</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={7} className="empty">
                  <span className="loading" />
                </td>
              </tr>
            ) : error ? (
              <tr>
                <td colSpan={7} className="empty">
                  <p style={{ color: "var(--red)" }}>{error}</p>
                </td>
              </tr>
            ) : filtered.length === 0 ? (
              <tr>
                <td colSpan={7} className="empty">
                  <p>{query ? `Aucun tenant ne correspond à « ${query} »` : "Aucun tenant. Créez-en un !"}</p>
                </td>
              </tr>
            ) : (
              filtered.map((t) => (
                <TenantRow
                  key={t.id}
                  tenant={t}
                  open={openMenuId === t.id}
                  onToggleMenu={(e) => {
                    e.stopPropagation();
                    setOpenMenuId((cur) => (cur === t.id ? null : t.id));
                  }}
                  onEdit={() => {
                    setOpenMenuId(null);
                    navigate(`/tenants/${t.id}/edit`);
                  }}
                  onRotateKey={() => {
                    setOpenMenuId(null);
                    askRotateKey(t);
                  }}
                  onFlushCache={() => {
                    setOpenMenuId(null);
                    askFlushCache(t);
                  }}
                  onDeactivate={() => {
                    setOpenMenuId(null);
                    askDeactivate(t);
                  }}
                  onActivate={() => {
                    setOpenMenuId(null);
                    void activate(t);
                  }}
                  onDelete={() => {
                    setOpenMenuId(null);
                    askDelete(t);
                  }}
                />
              ))
            )}
          </tbody>
        </table>
      </div>

      <ConfirmModal
        open={confirm !== null}
        title={confirm?.title}
        body={confirm?.body}
        extra={confirm?.extra}
        confirmLabel={confirm?.okLabel}
        danger={confirm?.danger}
        onConfirm={async () => {
          if (confirm) await confirm.onConfirm();
        }}
        onCancel={() => setConfirm(null)}
      />

      {rotatedKey && <KeyDisplayModal value={rotatedKey} onClose={() => setRotatedKey(null)} />}
    </div>
  );
}

// ---------------------------------------------------------------------------

interface RowProps {
  tenant: TenantWithSensitive;
  open: boolean;
  onToggleMenu: (e: React.MouseEvent) => void;
  onEdit: () => void;
  onRotateKey: () => void;
  onFlushCache: () => void;
  onDeactivate: () => void;
  onActivate: () => void;
  onDelete: () => void;
}

function TenantRow({
  tenant,
  open,
  onToggleMenu,
  onEdit,
  onRotateKey,
  onFlushCache,
  onDeactivate,
  onActivate,
  onDelete,
}: RowProps) {
  const btnRef = useRef<HTMLButtonElement>(null);
  const [dropPos, setDropPos] = useState<{ top: number; right: number } | null>(null);

  useLayoutEffect(() => {
    if (open && btnRef.current) {
      const r = btnRef.current.getBoundingClientRect();
      setDropPos({ top: r.bottom + 4, right: window.innerWidth - r.right });
    }
  }, [open]);

  return (
    <tr>
      <td>
        <div style={{ fontWeight: 600 }}>{tenant.name}</div>
        <div className="mono" style={{ fontSize: 11, marginTop: 2 }}>
          {tenant.slug}
        </div>
      </td>
      <td>
        <span className="mono" style={{ fontSize: 11 }}>
          {tenant.email ?? "—"}
        </span>
      </td>
      <td>
        <span className="mono">{tenant.llm_model}</span>
      </td>
      <td>
        {tenant.llm_configured ? (
          <span className="badge badge-green" style={{ display: "inline-flex", alignItems: "center", gap: 4 }}><Check size={11} /> Configuré</span>
        ) : (
          <span className="badge badge-red">Non configuré</span>
        )}
      </td>
      <td>
        <span className={"badge " + (tenant.is_active ? "badge-green" : "badge-red")}>
          {tenant.is_active ? "Actif" : "Inactif"}
        </span>
      </td>
      <td>
        <span className="mono" style={{ fontSize: 11 }}>
          {fmtDate(tenant.created_at)}
        </span>
      </td>
      <td>
        <button
          ref={btnRef}
          type="button"
          className={"row-menu-btn" + (open ? " active" : "")}
          onClick={onToggleMenu}
          title="Actions"
        >
          <MoreVertical size={16} />
        </button>
        {open && dropPos &&
          createPortal(
            <div
              id="row-actions-dropdown"
              className="open"
              style={{ top: dropPos.top, right: dropPos.right }}
              onClick={(e) => e.stopPropagation()}
            >
              <button className="row-menu-item" onClick={onEdit} style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <Pencil size={13} /> Modifier
              </button>
              <button className="row-menu-item warn" onClick={onRotateKey} style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <RotateCcw size={13} /> Rotation de clé
              </button>
              <button className="row-menu-item" onClick={onFlushCache} style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <Zap size={13} /> Vider le cache
              </button>
              <div className="row-menu-sep" />
              {tenant.is_active ? (
                <button className="row-menu-item danger" onClick={onDeactivate} style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <PauseCircle size={13} /> Désactiver
                </button>
              ) : (
                <button className="row-menu-item success" onClick={onActivate} style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <PlayCircle size={13} /> Activer
                </button>
              )}
              <button className="row-menu-item danger" onClick={onDelete} style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <Trash2 size={13} /> Supprimer
              </button>
            </div>,
            document.body,
          )}
      </td>
    </tr>
  );
}
